import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isTLA,
  loadUserTariffs,
  LocalizePropType,
  selectTariffs,
  selectUser,
  updateUser,
  withTextLocalizer,
  uploadDocument,
  selectBrandSettings,
  BrandData,
  getNextUserOpenChallengeRoute,
  isLocalizeAdded,
  selectNetworkActivities,
  CLIENT_TYPE,
  getAge,
} from 'getaway-data-layer';
import { useHistory } from 'react-router-dom';
import TariffView from './TariffView';
import {
  isDashboardScreen,
  navigateTo,
  showAlert,
  showAlertWithOptions,
} from '../../../../utils';
import { TARIFF_CONSENT_STATUS } from '../../../../enums/Tariff';
import { useFilePicker } from 'use-file-picker';

interface Values {}

type Props = {
  localize: LocalizePropType;
};

const TariffContainer = ({ localize }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const tariffs = useSelector(selectTariffs);
  const networkActivities: any = useSelector(selectNetworkActivities);
  const isLoadingTariffs = networkActivities.some((activityType: any) =>
    [CLIENT_TYPE.USER_CLIENT.LOAD_TARIFFS].includes(activityType)
  );
  const user = useSelector(selectUser);
  const brandSettings = useSelector(selectBrandSettings) as BrandData;

  const [tariffConsentStatus, setTariffConsentStatus] = useState(
    brandSettings?.tariffConsentRequired !== undefined &&
      brandSettings?.tariffConsentRequired
      ? TARIFF_CONSENT_STATUS.OPEN
      : TARIFF_CONSENT_STATUS.APPROVED
  );
  const [tariffSelected, setTariffSelected] = useState(null);
  const [upsellingSelected, setUpsellingSelected] = useState(null);
  const [tlaExtraTariffs, setTlaExtraTariffs] = useState([]);
  const [selectedTlaExtraOption, setSelectedTlaExtraOption] = useState(null);
  const displayTariffs = isTLA()
    ? selectedTlaExtraOption ||
      tariffs?.filter(
        (tariff) =>
          !(
            tariff?.name.startsWith('Rahmentarif') &&
            tariff?.id !== '6661aeeb02a17c06e80801c7'
          )
      )
    : tariffs;
  const [subscriptionNum, setSubscriptionNum] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const userAge = getAge(user.dateOfBirth);
  const minimalUserAge = brandSettings?.kycSettings?.minimalUserAge;
  const isUnderAge = userAge && minimalUserAge && userAge < minimalUserAge;

  const [openFileSelector, { plainFiles, loading, clear: clearFile }] =
    useFilePicker({
      accept: 'image/*',
      multiple: false,
      limitFilesConfig: { max: 1 },
    });
  const [fileError, setFileError] = useState('');

  const onTariffSelected = (tariff) => {
    setTariffSelected(tariff);
    setUpsellingSelected(null);
  };

  const onSubmit = () => {
    // Mandatory subscriptionNum if Marego-Abo or MOBIcar tariffs selected
    if (selectedTlaExtraOption) {
      if (
        !subscriptionNum &&
        selectedTlaExtraOption?.[0]?.id != '6661b99d02a17c06e80801dc'
      ) {
        setSubscriptionError(localize('validation.required'));
      }
      if (!plainFiles?.[0]) {
        setFileError(localize('validation.required'));
      }
      if (
        (!subscriptionNum &&
          selectedTlaExtraOption?.[0]?.id != '6661b99d02a17c06e80801dc') ||
        !plainFiles?.[0]
      ) {
        return;
      }
    }

    if (selectedTlaExtraOption && !plainFiles?.[0]) return;
    if (tariffSelected) {
      dispatch(
        // @ts-ignore
        updateUser(
          {
            ...(subscriptionNum && {
              extraData: JSON.stringify({ subscriptionNum: subscriptionNum }),
            }),
            tariff: {
              id: tariffSelected.id,
              upsellings:
                upsellingSelected || selectedTlaExtraOption
                  ? [
                      {
                        ...(upsellingSelected && { code: upsellingSelected }),
                      },
                    ]
                  : [],
            },
          },
          (updatedUser) => {
            if (plainFiles?.[0]) {
              dispatch(uploadDocument(plainFiles?.[0], 'OTHER', () => {}));
            }

            if (tariffSelected?.upsellings?.length > 0) {
              navigateTo(
                history,
                isDashboardScreen()
                  ? '/dashboard/upsellings'
                  : '/onboarding/upsellings'
              );
            } else {
              navigateTo(history, getNextUserOpenChallengeRoute(updatedUser));
            }
          },
          () => {},
          () => {
            navigateTo(history, '/settings');
          }
        )
      );
    }
  };

  const onClose = () => {
    if (isDashboardScreen()) {
      navigateTo(history, '/settings');
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onClickExtraOptions = () => {
    const options = tlaExtraTariffs.map((tariff) => ({
      title: isLocalizeAdded(localize, `settings.tariff.${tariff?.id}.name`)
        ? localize(`settings.tariff.${tariff?.id}.name`)
        : tariff.name,
      action: () => {
        setTariffSelected(tariff);
        setUpsellingSelected(null);
        setSelectedTlaExtraOption([tariff]);
      },
    }));
    showAlertWithOptions(localize('tariff.tla.extra'), '', options);
  };

  const onResetExtraOptions = () => {
    setTariffSelected(tariffs[0]);
    setUpsellingSelected(null);
    setSelectedTlaExtraOption(null);
    setSubscriptionError('');
    setFileError('');
  };

  const onChangeSubscriptionNum = (value) => {
    setSubscriptionNum(value);
    setSubscriptionError('');
  };

  useEffect(() => {
    setFileError('');
  }, [plainFiles?.[0]]);

  useEffect(() => {
    dispatch(loadUserTariffs());
  }, []);

  useEffect(() => {
    if (!user?.tariff?.id) {
      if (tariffs?.length >= 1) {
        setTariffSelected(tariffs[0]);
      }
    } else if (tariffs && user?.tariff?.id) {
      const isTlaExtraTariff =
        user?.tariff.name.startsWith('Rahmentarif') &&
        user?.tariff.id !== '6661aeeb02a17c06e80801c7';
      if (isTlaExtraTariff) {
        setSelectedTlaExtraOption(
          tariffs?.filter((tariff) => tariff?.id === user?.tariff?.id)
        );
        setTariffSelected(
          tariffs?.find((tariff) => tariff?.id === user?.tariff?.id)
        );
        const subscription = user.extraData
          ? JSON.parse(user.extraData)?.subscriptionNum
          : null;
        if (subscription) {
          setSubscriptionNum(subscription);
        }
      } else {
        setTariffSelected(
          tariffs?.find((tariff) => tariff?.id === user?.tariff?.id)
        );
      }
      setTariffConsentStatus(TARIFF_CONSENT_STATUS.APPROVED);
      if (user?.tariff?.upsellings) {
        setUpsellingSelected(user?.tariff?.upsellings?.[0]?.code);
      }
    }

    if (isTLA() && tariffs) {
      const filteredTariffs = tariffs.filter(
        (tariff) =>
          tariff.name.startsWith('Rahmentarif') &&
          tariff.id !== '6661aeeb02a17c06e80801c7'
      );
      setTlaExtraTariffs(filteredTariffs);
    }
  }, [tariffs, user]);

  return (
    <TariffView
      tariffs={displayTariffs}
      tlaExtraTariffs={tlaExtraTariffs}
      isOrg={user?.userType === 'org'}
      localize={localize}
      onClose={onClose}
      tariffSelected={tariffSelected}
      setTariffSelected={onTariffSelected}
      tariffConsentStatus={tariffConsentStatus}
      setTariffConsentStatus={setTariffConsentStatus}
      onSubmit={onSubmit}
      onClickExtraOptions={onClickExtraOptions}
      selectedTlaExtraOption={selectedTlaExtraOption}
      onResetExtraOptions={onResetExtraOptions}
      openFileSelector={openFileSelector}
      fileLoading={loading}
      filesContent={plainFiles?.[0]}
      clearFile={clearFile}
      subscriptionNum={subscriptionNum}
      setSubscriptionNum={onChangeSubscriptionNum}
      subscriptionError={subscriptionError}
      fileError={fileError}
      tariffConsentRequired={brandSettings?.tariffConsentRequired}
      isUnderAge={isUnderAge}
      isLoadingTariffs={isLoadingTariffs}
    />
  );
};

export default withTextLocalizer(TariffContainer);
